/**
 * Angular Component BMFileUploader
 * Drag & Drop File uploader
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';

import {
  FileStatus,
  FileUploaderFile,
  FileUploaderUploadEvent,
  ValidDataTypes,
} from '../uploaders.interfaces';

import { UploadEventsService } from '../upload-events-service/upload-events-service';

@Component({
  selector: 'bm-click-file-upload',
  templateUrl: 'bm-click-file-upload.component.html',
  styleUrls: ['bm-click-file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // tslint:disable-next-line:no-forward-ref
      useExisting: forwardRef(() => BMClickFileUploaderComponent),
      multi: true,
    },
    UploadEventsService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // tslint:disable-next-line:use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
})
export class BMClickFileUploaderComponent
  implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() public accept!: string;
  @Input() public dataType!: ValidDataTypes;
  @Input() public disabled!: boolean;
  @Input() public multiple!: boolean;

  @Output() public abort = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public done = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public empty = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public error = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public loading = new EventEmitter<FileUploaderUploadEvent>();

  @ViewChild('inputFile', { read: ElementRef, static: true })
  private _inputElement!: ElementRef<HTMLInputElement>;

  private _subs: Subscription = Subscription.EMPTY;

  private _onChangeCb!: (_: FileUploaderFile[]) => void;
  private _onTouchedCb!: () => void;

  constructor(private _uploadEventsService: UploadEventsService) {}

  public ngOnInit() {
    this._uploadEventsService.events$.subscribe({
      // tslint:disable-next-line:cyclomatic-complexity
      next: event => {
        switch (event.status) {
          case FileStatus.ABORT:
            this.abort.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb([]);
            }

            break;
          case FileStatus.DONE:
            this.done.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb(event.files.map(e => e.file));
            }

            break;
          case FileStatus.EMPTY:
            this.empty.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb([]);
            }

            break;
          case FileStatus.ERROR:
            this.error.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb([]);
            }

            break;
          case FileStatus.LOADING:
            this.loading.emit(event);

            if (this._onTouchedCb) {
              this._onTouchedCb();
            }
            break;
          default:
        }
      },
    });
  }

  public ngOnDestroy() {
    this._subs.unsubscribe();
  }

  public onFocusin() {
    if (this._onTouchedCb) {
      this._onTouchedCb();
    }
  }

  public writeValue(val: FileList | unknown) {
    if (val instanceof FileList) {
      this._uploadEventsService.readFiles(val, this.dataType);
    } else {
      this._inputElement.nativeElement.value = '';
      this._uploadEventsService.readFiles(null, this.dataType);
    }
  }

  public registerOnChange(fn: (_: FileUploaderFile[]) => void) {
    this._onChangeCb = fn;
  }

  public registerOnTouched(fn: () => void) {
    this._onTouchedCb = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
